import { Modal, RemoveScroll, ScrollArea, Table, TextInput, Grid, Button, Autocomplete } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { memo, useEffect, useMemo, useState } from 'react';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';
import ProductType from '../../../enum/ProductType';
import { useFormContext } from 'react-hook-form';
import { useForm } from '@mantine/form';
import { IOrderProduct } from '../../../interfaces/Product';
import { GarmentAttributeCatalog } from '../../../interfaces/Company';
import {
  getGarmentAttributeCatalogValues,
  resolveGarmentAttributeCatalogs
} from '../../../utils/garmentAttributeCatalogs';

const getProductLineKey = (prod: any, idx: number): string => {
  return String(prod?.lineId || prod?.orderProductLineID || prod?._id || `prod-${idx}`);
};

// Build an attributes map keyed by line identity so duplicate product lines stay isolated
const buildAttributesMap = (order: any) => {
  const attributesMap: Record<string, {
    productIndex: number;
    productInfo: any;
    attributesArr: {
      color: string;
      damage: string;
      stain: string;
      brand: string;
      notes: string;
    }[];
  }> = {};

  const products: IOrderProduct[] = order?.products || [];

  products.forEach((prod: IOrderProduct, idx: number) => {
    if (!prod || !prod._id || prod.returnSourceOrderLineId) return;
    const key = getProductLineKey(prod, idx);

    attributesMap[key] = {
      productIndex: idx,
      productInfo: prod,
      attributesArr: []
    };

    const isSingleAttributes = prod.type === ProductType.WEIGHT || prod.type === ProductType.LENGTH;
    const quantity = isSingleAttributes ? 1 : prod.quantity;

    for (let i = 0; i < quantity; i++) {
      attributesMap[key].attributesArr[i] = prod?.attributes?.[i] || {
        color: '',
        damage: '',
        stain: '',
        brand: '',
        notes: ''
      } as any;
    }
  });

  return attributesMap;
};

export default memo(function AddAttributesModal({
  opened,
  close,
  title,
  garmentAttributeCatalogs,
}: {
  opened: boolean;
  close: () => void;
  title: string;
  garmentAttributeCatalogs: GarmentAttributeCatalog[];
}) {
  const { t } = useTranslation(['OrdersPage']);
  const { getValues, setValue } = useFormContext();
  const resolvedGarmentAttributeCatalogs = useMemo(
    () => resolveGarmentAttributeCatalogs(garmentAttributeCatalogs),
    [garmentAttributeCatalogs]
  );
  const colors = useMemo(
    () => getGarmentAttributeCatalogValues(resolvedGarmentAttributeCatalogs, 'color'),
    [resolvedGarmentAttributeCatalogs]
  );
  const damages = useMemo(
    () => getGarmentAttributeCatalogValues(resolvedGarmentAttributeCatalogs, 'damage'),
    [resolvedGarmentAttributeCatalogs]
  );
  const stains = useMemo(
    () => getGarmentAttributeCatalogValues(resolvedGarmentAttributeCatalogs, 'stain'),
    [resolvedGarmentAttributeCatalogs]
  );
  const brands = useMemo(
    () => getGarmentAttributeCatalogValues(resolvedGarmentAttributeCatalogs, 'brand'),
    [resolvedGarmentAttributeCatalogs]
  );

  const form = useForm({
    initialValues: {},
    validate: {}
  });
  const [attributesMap, setAttributesMap] = useState<ReturnType<typeof buildAttributesMap> | null>(null);
  const formValues = form.values as Record<
    string,
    {
      productIndex: number;
      productInfo: any;
      attributesArr: Array<Record<string, string>>;
    }
  >;

  const getAttributeFieldValue = (
    productKey: string,
    attributeIndex: number,
    field: 'color' | 'damage' | 'stain' | 'brand' | 'notes'
  ) => formValues?.[productKey]?.attributesArr?.[attributeIndex]?.[field] || '';

  const handleAttributeFieldChange = (
    productKey: string,
    attributeIndex: number,
    field: 'color' | 'damage' | 'stain' | 'brand' | 'notes',
    value: string
  ) => {
    form.setFieldValue(`${productKey}.attributesArr.${attributeIndex}.${field}`, value);
  };

  useEffect(() => {
    if (!opened) {
      setAttributesMap(null);
      return;
    }

    const nextAttributesMap = buildAttributesMap({
      products: getValues('products') || []
    });

    setAttributesMap(nextAttributesMap);
    form.setValues(nextAttributesMap);
  }, [opened]);

  const submit = (values: any) => {
    try {
      const currentProducts = getValues('products');
      const updatedProducts = [
        ...(Array.isArray(currentProducts) ? currentProducts : [])
      ];

      Object.keys(values).forEach((productKey: string) => {
        const productIndex: number | undefined = values[productKey]?.productIndex;
        if (productIndex === undefined || productIndex === null) return;

        const productVals = values[productKey];
        const productFound = updatedProducts[productIndex];
        if (!productFound || productFound.returnSourceOrderLineId) return;

        const isMeasuredLine =
          productVals?.productInfo?.type === ProductType.WEIGHT ||
          productVals?.productInfo?.type === ProductType.LENGTH;
        const nextAttributes = isMeasuredLine
          ? [productVals.attributesArr?.[0] || {}]
          : [...(productVals.attributesArr || [])];

        productFound.attributes = nextAttributes.map((attrs: any) => ({
          ...attrs
        }));
      });
      setValue('products', updatedProducts);
      close();
    } catch (e) {
      console.log('Algo salio mal: ', e);
    }
  };

  const attributesRows =
    !opened || !attributesMap
      ? null
      : Object.keys(attributesMap).map((mapKey) => {
          return attributesMap[mapKey].attributesArr.map((productRowAttributes, idx) => {
            const { name } = attributesMap[mapKey].productInfo;
            return (
              <Table.Tr key={`${mapKey}-${idx}`}>
                <Table.Td>{name}</Table.Td>
                <Table.Td>
                  <Autocomplete
                    placeholder={t('OrdersPage:productsModal.colors')}
                    data={colors}
                    value={getAttributeFieldValue(mapKey, idx, 'color')}
                    onChange={(value) => handleAttributeFieldChange(mapKey, idx, 'color', value)}
                  />
                </Table.Td>
                <Table.Td>
                  <Autocomplete
                    placeholder={t('OrdersPage:productsModal.damage')}
                    data={damages}
                    value={getAttributeFieldValue(mapKey, idx, 'damage')}
                    onChange={(value) => handleAttributeFieldChange(mapKey, idx, 'damage', value)}
                  />
                </Table.Td>
                <Table.Td>
                  <Autocomplete
                    placeholder={t('OrdersPage:productsModal.stain')}
                    data={stains}
                    value={getAttributeFieldValue(mapKey, idx, 'stain')}
                    onChange={(value) => handleAttributeFieldChange(mapKey, idx, 'stain', value)}
                  />
                </Table.Td>
                <Table.Td>
                  <Autocomplete
                    placeholder={t('OrdersPage:productsModal.brand')}
                    data={brands}
                    value={getAttributeFieldValue(mapKey, idx, 'brand')}
                    onChange={(value) => handleAttributeFieldChange(mapKey, idx, 'brand', value)}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    placeholder={t('OrdersPage:productsModal.notes')}
                    value={getAttributeFieldValue(mapKey, idx, 'notes')}
                    onChange={(event) =>
                      handleAttributeFieldChange(mapKey, idx, 'notes', event.currentTarget.value)
                    }
                  />
                </Table.Td>
              </Table.Tr>
            );
          });
        });

  return (
    <Modal
      opened={opened}
      onClose={close}
      className={RemoveScroll.classNames.fullWidth}
      title={title}
      scrollAreaComponent={ScrollArea.Autosize}>
      {!attributesMap ? (
        <LoaderOverlay visible />
      ) : (
        <form onSubmit={form.onSubmit(submit)}>
          <Grid gutter={16}>
            <Grid.Col span={{ base: 12 }}>
              <Table striped highlightOnHover withTableBorder withColumnBorders width={'100%'}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('OrdersPage:productsModal.product')}</Table.Th>
                    <Table.Th>{t('OrdersPage:productsModal.colors')}</Table.Th>
                    <Table.Th>{t('OrdersPage:productsModal.damage')}</Table.Th>
                    <Table.Th>{t('OrdersPage:productsModal.stain')}</Table.Th>
                    <Table.Th>{t('OrdersPage:productsModal.brand')}</Table.Th>
                    <Table.Th>{t('OrdersPage:productsModal.notes')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{attributesRows}</Table.Tbody>
              </Table>
            </Grid.Col>
            <Grid.Col span={12} pos={'sticky'} bottom={0}>
              <Button type="submit" fullWidth loading={false}>
                {t('CommonButtons:submit')}
              </Button>
            </Grid.Col>
          </Grid>
        </form>
      )}
    </Modal>
  );
});
